import type { ComponentChildren } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import { api } from "../../api/client.ts";
import type { CommentOut, Conversation, Guest, SpaceOut } from "../../api/types.gen.ts";
import { t } from "../../i18n/index.ts";
import { refreshCommentCounts } from "../../lib/commentCounts.ts";
import {
  aboutOf,
  type ComposerAction,
  closeActions,
  countOf,
  type DiscussionCtx,
  inThread,
  isResolved,
  nest,
  sendReply,
  speakersOf,
  subject,
  type ThreadRef,
  type Topic,
} from "../../lib/conversation.ts";
import { ignore } from "../../lib/ignore.ts";
import { outsideOf, spaceApi } from "../../lib/paths.ts";
import { isReport, REPORT_EDGE, type Series, seriesOf } from "../../lib/reports.ts";
import type { Resource } from "../../lib/resource.ts";
import { herePath } from "../../lib/router.ts";
import { canComment, me } from "../../lib/state.ts";
import { InlineButton, Link } from "../../ui/Button.tsx";
import { CommentIcon } from "../../ui/icons.tsx";
import { buttonClass } from "../../ui/styles.ts";
import { authPath } from "../auth/flow.ts";
import { CommentView } from "./comment.tsx";
import { Composer } from "./composer.tsx";
import { SeriesView } from "./report.tsx";

/** A first comment with its replies under it, and (when asked) a box to reply. */
function Discussion({
  ctx,
  at,
  root,
  replies,
}: {
  ctx: DiscussionCtx;
  at: ThreadRef;
  root: CommentOut;
  replies: readonly CommentOut[];
}) {
  const [replying, setReplying] = useState(false);
  return (
    <li
      class={`flex flex-col gap-3 rounded-md border border-zinc-200 bg-white p-3 ${isReport(root) ? REPORT_EDGE : ""}`}
    >
      <CommentView ctx={ctx} c={root} onReply={() => setReplying(true)} />
      {replies.length > 0 || replying ? (
        <ul class="ml-3 flex flex-col gap-3 border-l border-zinc-200 pl-3">
          {replies.map((r) => (
            <li key={r.id}>
              <CommentView ctx={ctx} c={r} onReply={() => setReplying(true)} />
            </li>
          ))}
          {replying ? (
            <li>
              <Composer
                space={ctx.space}
                about={aboutOf(ctx)}
                placeholder={t("comments.replyPlaceholder")}
                sendLabel={t("comments.reply")}
                autoFocus={true}
                onCancel={() => setReplying(false)}
                send={async (body) => {
                  await sendReply(ctx, at, root, body);
                  setReplying(false);
                }}
              />
            </li>
          ) : null}
        </ul>
      ) : null}
    </li>
  );
}

/**
 * A thread of an item's conversation (the item's own, a review's, or a finding's): its discussions, oldest first,
 * the ones new to you marked, and a box to start another. Opening it marks it seen (the marks stay while it's open).
 */
export function Thread({
  space,
  topic,
  conv,
  at,
  actions,
  empty,
  compact = false,
  guests = [],
  pinReports = false,
}: {
  space: SpaceOut;
  /** Whose conversation: an item's, rule's, entity's, or (none) the space's channel. */
  topic: Topic;
  /** The item's guests (mentionable too). */
  guests?: readonly Guest[];
  conv: Resource<Conversation>;
  at: ThreadRef;
  /** Other ways to send (a finding's: closing it with the comment as the reason). */
  actions?: readonly ComposerAction[] | undefined;
  /** Said when it has no comments yet (nothing: nothing said). */
  empty?: string | undefined;
  /** Inside a finding's or review's card. */
  compact?: boolean;
  /** The latest report of each series above the rest, pinned while it's the newest (an item page's conversation). */
  pinReports?: boolean;
}) {
  const count = countOf(conv.data?.threads, at);
  // What its reader had seen when it opened: taken once the conversation is in (on a page opened at it, it isn't yet).
  const opened = useRef<{ seen: string | null | undefined } | null>(null);
  if (conv.data && !opened.current) {
    opened.current = { seen: count?.seen };
  }
  const seen = opened.current?.seen;
  const unread = count?.unread ?? 0;
  const marked = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: marked once, when there's something new to mark.
  useEffect(() => {
    if (unread > 0 && !marked.current && !outsideOf(space)) {
      marked.current = true;
      api
        .post(spaceApi(space, "/comments/seen"), subject(topic, at))
        .then(() => {
          conv.reload();
          refreshCommentCounts();
        })
        .catch(ignore);
    }
  }, [unread]);
  const said = conv.data?.data ?? [];
  const people = new Set(said.flatMap((c) => (c.author ? [c.author] : [])));
  const speakers = speakersOf(said, guests);
  const ctx: DiscussionCtx = { space, topic, reload: conv.reload, people, speakers, seen };
  const list = (conv.data?.data ?? []).filter((c) => inThread(c, at));
  // Threads on places in its file that are resolved: out of the way unless asked for.
  const [resolvedShown, setResolvedShown] = useState(false);
  const all = nest(list);
  const resolved = all.filter((d) => d.root.anchor && isResolved(d.replies)).length;
  const shown = all.filter((d) => resolvedShown || !(d.root.anchor && isResolved(d.replies)));
  // Reports of a label as one card, the newest; pinned, the labelled ones above the rest.
  type D = (typeof shown)[number];
  const series = seriesOf(shown, (d) => d.root);
  const pinned = pinReports ? series.filter((s) => isReport(s.latest.root) && s.latest.root.label) : [];
  const rest = pinReports ? series.filter((s) => !pinned.includes(s)) : series;
  const draw = (s: Series<D>) => (
    <SeriesView
      key={s.latest.root.id}
      series={s}
      rootOf={(d) => d.root}
      draw={(d) => <Discussion key={d.root.id} ctx={ctx} at={at} root={d.root} replies={d.replies} />}
    />
  );
  return (
    <div class="flex flex-col gap-3">
      {list.length === 0 && empty ? <p class="text-sm text-zinc-500">{empty}</p> : null}
      {resolved > 0 ? (
        <div class="flex justify-end">
          <InlineButton size="xs" aria-pressed={resolvedShown} onClick={() => setResolvedShown(!resolvedShown)}>
            {t("notes.showResolved", { count: resolved })}
          </InlineButton>
        </div>
      ) : null}
      {pinned.length > 0 ? (
        <section class="flex flex-col gap-2" aria-label={t("reports.latest")} data-pinned-reports="">
          <h3 class="text-xs font-semibold text-zinc-500 uppercase">{t("reports.latest")}</h3>
          <ul class="flex flex-col gap-2">{pinned.map(draw)}</ul>
        </section>
      ) : null}
      {rest.length > 0 ? <ul class="flex flex-col gap-2">{rest.map(draw)}</ul> : null}
      {canComment(space) ? (
        <Composer
          space={space}
          about={{ item: topic.item, people, speakers }}
          compact={compact}
          placeholder={compact ? t("comments.placeholderShort") : t("comments.placeholder")}
          sendLabel={t("comments.send")}
          actions={actions}
          send={async (body) => {
            await api.post(spaceApi(space, "/comments"), { ...subject(topic, at), body });
            conv.reload();
          }}
        />
      ) : (
        <SignInToComment space={space} />
      )}
    </div>
  );
}

/** Outside the space, signed out, where it takes comments: a way in to comment. */
function SignInToComment({ space }: { space: SpaceOut }) {
  const out = outsideOf(space);
  if (!out?.shows.comments || me.value !== null) {
    return null;
  }
  return (
    <div>
      <Link href={authPath("/signin", {}, herePath())} class={buttonClass()}>
        {t("public.signInToComment")}
      </Link>
    </div>
  );
}

/** Opens a thread in place: its comments' count (and how many are new), or just the icon when it has none. */
function ThreadToggle({
  conv,
  at,
  open,
  onToggle,
}: {
  conv: Resource<Conversation> | undefined;
  at: ThreadRef;
  open: boolean;
  onToggle: () => void;
}) {
  const count = countOf(conv?.data?.threads, at);
  const total = count?.total ?? 0;
  const unread = count?.unread ?? 0;
  return (
    <InlineButton size="xs" aria-expanded={open} onClick={onToggle} icon={<CommentIcon size={14} />}>
      {total > 0 ? t("comments.count", { count: total }) : t("comments.add")}
      {unread > 0 ? (
        <span class="font-medium text-blue-700">{` · ${t("comments.unread", { count: unread })}`}</span>
      ) : null}
    </InlineButton>
  );
}

/** A finding's thread can close it (resolved, or dismissed), the comment as the reason. */
interface Closing {
  item: string;
  finding: string;
  asOf: string | undefined;
  /** After it's closed (the thread's conversation is reloaded first). */
  onChange: () => void;
}

/**
 * A thread opened in place from its row (a finding's, a review's, a field's): the row is drawn by `children`, given the
 * thread's `toggle` (how many comments, opening it) for where the row puts it, and the `thread` once open, for under
 * the row. Without a conversation, neither.
 */
export function InlineThread({
  children,
  ...o
}: {
  space: SpaceOut;
  topic: Topic;
  conv: Resource<Conversation> | undefined;
  at: ThreadRef;
  close?: Closing | null | undefined;
  children: (toggle: ComponentChildren, thread: ComponentChildren) => ComponentChildren;
}) {
  const [open, setOpen] = useState(false);
  const { conv, close } = o;
  if (!conv) {
    return <>{children(null, null)}</>;
  }
  const actions = close
    ? closeActions({
        space: o.space,
        item: close.item,
        finding: close.finding,
        asOf: close.asOf,
        onDone: () => {
          conv.reload();
          close.onChange();
        },
      })
    : undefined;
  return (
    <>
      {children(
        <ThreadToggle conv={conv} at={o.at} open={open} onToggle={() => setOpen(!open)} />,
        open ? <Thread compact={true} space={o.space} topic={o.topic} conv={conv} at={o.at} actions={actions} /> : null,
      )}
    </>
  );
}
